import type { NetworkPoiPipelineRow, NetworkPoiPipelinesDownload } from '@/api/poiPipelinesApi';
import { formatNetFunc } from '@/components/map/utils/pipelineFeatureProperties';
import { buildEntityExportRow, buildPoiExportHeaders } from './poiExport';
import type { EntityExportRow } from './poiExport';
import { exportSheetsToFile, type ExportFormat } from './exportWorkbook';

export function buildPoiPipelinesExportHeaders(): string[] {
    return [
        'Area',
        'DMA',
        'DMA Name',
        'Worklist',
        'Stop',
        'W3W',
        'Pipe ID',
        'Pipe Type',
        'Length In Radius (m)',
        'POI Total Length (m)',
        'Radius (m)',
    ];
}

function round2(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * One worksheet row per POI-pipe association, for each POI entity in the export selection
 * (leak rows are not POIs and carry no breakdown of their own). A POI with no pipelines
 * inside its radius still gets one explicit row — blank pipe fields, a zero total and the
 * radius the calculation used — so every exported POI states a total and the empty case is
 * unambiguous rather than silently absent.
 *
 * The repeated per-POI total is the sum of the rounded per-pipe lengths, the same rule the
 * POI panel uses, so the worksheet reconciles with itself and with the portal display.
 */
export function buildPoiPipelinesExportRows(
    entityRows: EntityExportRow[],
    download: NetworkPoiPipelinesDownload,
): unknown[][] {
    const byPoi = new Map<string, NetworkPoiPipelineRow[]>();
    for (const row of download.pipelines) {
        const existing = byPoi.get(row.poiId);
        if (existing) existing.push(row);
        else byPoi.set(row.poiId, [row]);
    }

    const out: unknown[][] = [];
    for (const { entityType, item } of entityRows) {
        if (entityType !== 'poi') continue;

        const rows = byPoi.get(item.id) ?? [];
        if (rows.length === 0) {
            out.push([
                item.areaName,
                item.dmaIdent,
                item.dmaName,
                item.groupName,
                String(item.stop),
                item.whatThreeWords,
                '',
                '',
                0,
                0,
                download.radiusMetres,
            ]);
            continue;
        }

        const roundedLengths = rows.map((row) => round2(row.lengthMetres));
        const total = round2(roundedLengths.reduce((sum, length) => sum + length, 0));
        rows.forEach((row, index) => {
            out.push([
                item.areaName,
                item.dmaIdent,
                item.dmaName,
                item.groupName,
                String(item.stop),
                item.whatThreeWords,
                row.pipelineId,
                formatNetFunc(row.netFunc),
                roundedLengths[index],
                total,
                row.radiusMetres,
            ]);
        });
    }
    return out;
}

/**
 * Generates and triggers the POI download with the associated-pipelines breakdown included:
 * the unchanged "POIs & Leaks" sheet plus a "Pipelines" worksheet (a second CSV file when
 * the CSV format is chosen — see exportSheetsToFile).
 */
export async function exportPoisWithPipelines(
    rows: EntityExportRow[],
    download: NetworkPoiPipelinesDownload,
    fileName: string,
    format: ExportFormat,
): Promise<boolean> {
    return exportSheetsToFile({
        sheets: [
            { sheetName: 'POIs & Leaks', headers: buildPoiExportHeaders(), rows: rows.map(buildEntityExportRow) },
            {
                sheetName: 'Pipelines',
                headers: buildPoiPipelinesExportHeaders(),
                rows: buildPoiPipelinesExportRows(rows, download),
            },
        ],
        fileName,
        format,
    });
}
